/*
 * Hazelicious - liquid hover
 *
 * One coloured blob per card group, travelling between cards as the cursor moves.
 * Instead of each card lighting up on its own, the highlight is a single shape
 * that slides from the card you were on to the card you are on, stretching in the
 * direction of travel and settling with one soft wobble - liquid being poured
 * from one spot to the next. Its colour cross-fades to the new card's accent.
 *
 * Used by the header mega-menus and the Shop Our Products cards. Each caller
 * passes its own container, card selector and accent-colour variable; everything
 * else is shared.
 *
 * HOVER ZONE vs BLOB SHAPE
 * itemSelector is the shape the blob takes. triggerSelector, when given, is the
 * larger area that counts as hovering that shape - the product cards need this
 * because Add to Cart sits OUTSIDE the coloured rectangle but inside the card.
 * Callers that pass no triggerSelector behave exactly as before.
 *
 * The trigger currently under the blob carries the class .hz-liquid-hovered.
 * Sections key their text-colour flip off that as well as :hover, so the flip
 * tracks the blob rather than the cursor - see the note by markHovered().
 *
 * HOW THE MOTION WORKS
 * x, y, width and height each run a slightly under-damped spring toward the
 * hovered card's box. Under-damping is what gives the one small overshoot at
 * arrival; the stretch is the velocity itself, mapped to a scale along the axis
 * of travel with a matching squash across it. Nothing is keyframed, so a cursor
 * that changes mind mid-flight just bends the blob toward the new target.
 *
 * WHY THE BLOB PAINTS BEHIND THE CARD
 * The blob is one element per GROUP, the card's text and image belong to the
 * card. The cards are lifted with position: relative + z-index above the blob, so
 * the blob reads as the card's own background arriving - not a sheet sliding over
 * the artwork.
 *
 * FALLBACK
 * The sections keep their original instant :hover CSS. This script adds a class
 * to the container when it takes over, and the sections' CSS uses that class to
 * switch the instant highlight off. No JS, or prefers-reduced-motion, means the
 * class never appears and hovering behaves exactly as before.
 */
(function () {
  'use strict';

  var STIFFNESS = 340;   // spring constant - higher arrives faster
  var DAMPING = 26;      // just under critical for one gentle settle wobble
  var STRETCH = 0.0009;  // velocity (px/s) -> stretch; ~0.18 at full flight
  var STRETCH_MAX = 0.16;
  var FADE_MS = 160;     // blob appear/disappear at the group's edge

  function attach(container, opts) {
    if (!container || container.__hzLiquid) return;
    if (window.matchMedia) {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    }
    container.__hzLiquid = true;

    /* Where the blob LIVES can differ from where the cards live. The products
       grid clips its overflow (it is a horizontal scroller), and the stretch
       makes the blob temporarily wider than its card - on the outermost cards
       that overhang was sliced flat at the clip edge. Hosting the blob in an
       unclipped ancestor lets the bulge breathe past the edge instead. */
    var host = opts.host || container;

    var blob = document.createElement('div');
    blob.className = 'hz-liquid-blob';
    blob.setAttribute('aria-hidden', 'true');
    blob.style.cssText =
      'position:absolute;left:0;top:0;pointer-events:none;opacity:0;' +
      'border-radius:' + (opts.radius || 'var(--haz-radius)') + ';' +
      'transition:background-color 220ms ease,opacity ' + FADE_MS + 'ms ease;' +
      'will-change:transform;';
    if (getComputedStyle(host).position === 'static') {
      host.style.position = 'relative';
    }
    host.appendChild(blob);
    // Tells the section's CSS to hand the highlight over to the blob.
    container.classList.add('hz-liquid-on');

    // Spring state. Width/height are sprung too, so travelling between cards of
    // different sizes morphs the shape rather than snapping it.
    var s = { x: 0, y: 0, w: 0, h: 0, vx: 0, vy: 0, vw: 0, vh: 0 };
    var target = null, visible = false, raf = 0, lastNow = 0;

    function boxOf(item) {
      var r = item.getBoundingClientRect();
      var c = host.getBoundingClientRect();
      return {
        x: r.left - c.left + host.scrollLeft,
        y: r.top - c.top + host.scrollTop,
        w: r.width,
        h: r.height
      };
    }

    function colorOf(item) {
      var v = getComputedStyle(item).getPropertyValue(opts.colorVar).trim();
      return v || opts.fallbackColor || 'rgba(0,0,0,0.08)';
    }

    function render() {
      // The stretch: speed along each axis becomes elongation along it and a
      // matching squash across it, which is the entire liquid read. Direction
      // does not matter - a blob moving left bulges the same as one moving right.
      var ex = Math.min(STRETCH_MAX, Math.abs(s.vx) * STRETCH);
      var ey = Math.min(STRETCH_MAX, Math.abs(s.vy) * STRETCH);
      var sx = 1 + ex - ey * 0.6;
      var sy = 1 + ey - ex * 0.6;

      blob.style.width = s.w + 'px';
      blob.style.height = s.h + 'px';
      blob.style.transform =
        'translate(' + s.x + 'px,' + s.y + 'px) scale(' + sx + ',' + sy + ')';
    }

    function step(dt) {
      var settled = true;
      var axes = [['x', 'vx', 'x'], ['y', 'vy', 'y'], ['w', 'vw', 'w'], ['h', 'vh', 'h']];
      for (var i = 0; i < axes.length; i++) {
        var p = axes[i][0], v = axes[i][1];
        var goal = target[p];
        var acc = STIFFNESS * (goal - s[p]) - DAMPING * s[v];
        s[v] += acc * dt;
        s[p] += s[v] * dt;
        if (Math.abs(goal - s[p]) > 0.5 || Math.abs(s[v]) > 0.5) settled = false;
      }
      return settled;
    }

    function frame(now) {
      raf = 0;
      var dt = lastNow ? Math.min((now - lastNow) / 1000, 0.05) : 1 / 60;
      lastNow = now;
      var done = step(dt);
      render();
      if (!done && visible) {
        raf = requestAnimationFrame(frame);
      } else {
        lastNow = 0;
        // Kill any residual stretch so the resting shape is exactly the card.
        s.vx = s.vy = s.vw = s.vh = 0;
        render();
      }
    }

    function wake() {
      if (!raf) { lastNow = 0; raf = requestAnimationFrame(frame); }
    }

    function moveTo(item) {
      target = boxOf(item);
      blob.style.backgroundColor = colorOf(item);
      if (!visible) {
        // Entering the group from outside: no cross-page travel, the blob wells
        // up on the card itself.
        s.x = target.x; s.y = target.y; s.w = target.w; s.h = target.h;
        s.vx = s.vy = s.vw = s.vh = 0;
        render();
        visible = true;
        blob.style.opacity = '1';
      } else {
        wake();
      }
    }

    function hide() {
      visible = false;
      blob.style.opacity = '0';
      markHovered(null);
      if (raf) { cancelAnimationFrame(raf); raf = 0; lastNow = 0; }
    }

    /* The area that counts as "on this card" can be wider than the shape the blob
       takes. On the product cards Add to Cart is a sibling outside the coloured
       rectangle, so with the rectangle as the only trigger the blob went stale the
       moment the cursor reached the button: the fill stayed painted while the
       card's :hover - and with it the white text flip - had already let go. */
    var triggerSelector = opts.triggerSelector || opts.itemSelector;

    /* The blob and the card's text colour have to agree, and CSS :hover cannot
       keep that promise: it lets go the instant the cursor is in the gutter
       BETWEEN two cards, while the blob is still mid-flight. For those frames the
       fill was painted under text that had already flipped back to dark. So the
       sections key their text flip off this class as well as :hover - it is set
       when the blob moves to a card and cleared only when the blob hides, so the
       two can no longer disagree. */
    var hovered = null;

    function markHovered(trigger) {
      if (hovered === trigger) return;
      if (hovered) hovered.classList.remove('hz-liquid-hovered');
      hovered = trigger;
      if (hovered) hovered.classList.add('hz-liquid-hovered');
    }

    container.addEventListener('mouseover', function (e) {
      var trigger = e.target.closest(triggerSelector);
      if (!trigger || !container.contains(trigger)) return;
      var item = trigger.matches(opts.itemSelector)
        ? trigger
        : trigger.querySelector(opts.itemSelector);
      if (!item) return;
      markHovered(trigger);
      moveTo(item);
    });
    container.addEventListener('mouseleave', hide);
    // A scroll under the cursor (products carousel arrows) shifts every card;
    // land the blob on wherever its card is now rather than animating the jump.
    container.addEventListener('scroll', hide, { passive: true });
  }

  window.HazLiquid = { attach: attach };
})();
